import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../components/Dashboard/Doctor/DoctorDashboard.css';
import {
  Calendar, Clock, LogOut, Users, Mail, Loader2, Search, Phone, Video, MapPin,
  AlertCircle, Eye, ClipboardList, MessageCircle, Bell, Trash2, CheckCircle2, Plus, Send,
} from 'lucide-react';
import config from '../../config';
import { formatPhoneDisplay } from '../../utils/countryCodes';

// Nurse portal: READ-ONLY. The server only accepts GET with a nurse token and only returns
// patients of the doctors this nurse is linked to, so there are no action buttons here.
// Route:  <Route path="/nurse/dashboard" element={<NurseDashboard />} />
// Place this file next to DoctorDashboard.jsx (it reuses DoctorDashboard.css).

const TABS = [
  { id: 'upcoming', label: 'Upcoming', icon: Calendar },
  { id: 'pending', label: 'Pending', icon: Clock },
  { id: 'completed', label: 'Completed', icon: CheckCircle2 },
  { id: 'patients', label: 'Patients', icon: Users },
  { id: 'support', label: 'Support', icon: MessageCircle },
];
const APPT_TABS = ['upcoming', 'pending', 'completed'];
const TAB_TITLES = { upcoming: 'Upcoming appointments', pending: 'Pending appointments', completed: 'Completed appointments', patients: 'Patients', support: 'Support inbox' };

async function apiSend(method, path, token, body) {
  const res = await fetch(`${config.baseURL}/api${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || json.message || `Request failed (${res.status})`);
  return json.data;
}

async function apiGet(path, token) {
  const res = await fetch(`${config.baseURL}/api${path}`, {
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || body.message || `Request failed (${res.status})`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  const json = await res.json();
  return json.data;
}

function formatDate(d) {
  const datePart = String(d).slice(0, 10);
  return new Date(`${datePart}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
}
function formatTime(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':');
  const hour = ((+h + 11) % 12) + 1;
  return `${hour}:${m} ${+h < 12 ? 'AM' : 'PM'}`;
}
function initials(name) {
  if (!name) return '?';
  return name.split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();
}

const TYPE_META = {
  zoom: { label: 'Zoom video call', Icon: Video },
  phone: { label: 'Phone call', Icon: Phone },
  physical: { label: 'In-person visit', Icon: MapPin },
};
const typeMeta = (t) => TYPE_META[t] || TYPE_META.physical;

function ReadOnlyBanner() {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', margin: '0 0 12px',
        background: '#EEF4FA', border: '1px solid #C9DCEE', borderRadius: 8, color: '#0F4C75', fontSize: 13,
      }}
    >
      <Eye size={15} style={{ flexShrink: 0 }} />
      <span>You can reply to patient support, add or remove your own reminders, and view patients and appointments. Appointment changes are made by the doctor.</span>
    </div>
  );
}

function Sidebar({ nurse, activeTab, onTabChange, onLogout }) {
  return (
    <aside className="dd-sidebar">
      <div className="dd-profile">
        <div className="dd-avatar">{initials(nurse?.name)}</div>
        <p className="dd-profile-name">{nurse?.name || 'Loading…'}</p>
        <p className="dd-profile-specialty">Nurse</p>
      </div>

      <nav className="dd-nav">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button
              key={t.id}
              onClick={() => onTabChange(t.id)}
              className={`dd-nav-item ${activeTab === t.id ? 'active' : ''}`}
            >
              <Icon size={16} /> {t.label}
            </button>
          );
        })}
      </nav>

      {nurse?.doctors?.length > 0 && (
        <div style={{ padding: '0 16px 12px', fontSize: 12, color: '#6B7280' }}>
          <p style={{ margin: '0 0 4px', fontWeight: 600 }}>Your doctors</p>
          {nurse.doctors.map((d) => (
            <p key={d.id} style={{ margin: '0 0 2px' }}>{d.name}</p>
          ))}
        </div>
      )}

      <button onClick={onLogout} className="dd-logout">
        <LogOut size={16} /> Log out
      </button>
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Appointments (read-only)
// ---------------------------------------------------------------------------
function AppointmentsView({ token, status, onOpenPatient }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiGet(`/nurse/appointments?status=${status}`, token)
      .then(setItems)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, status]);

  return (
    <div className="section-stack">
      {loading ? (
        <div className="loading-row"><Loader2 size={16} className="spin" /> Loading…</div>
      ) : error ? (
        <p className="error-text">{error}</p>
      ) : items.length === 0 ? (
        <p className="muted-text">No {status} appointments.</p>
      ) : (
        <div className="list-stack">
          {items.map((a) => {
            const meta = typeMeta(a.appointment_type);
            const Icon = meta.Icon;
            return (
              <div key={a.id} className="row-card">
                <div className="row-content">
                  <p className="row-title" style={{ marginBottom: 2 }}>
                    <button
                      type="button"
                      onClick={() => onOpenPatient(a.patient_id)}
                      style={{ background: 'none', border: 'none', padding: 0, color: '#0F4C75', cursor: 'pointer', font: 'inherit', fontWeight: 600, textAlign: 'left' }}
                    >
                      {a.patient_name}
                    </button>
                  </p>
                  <p className="row-meta">
                    <Calendar size={11} /> {formatDate(a.appointment_date)}
                    <Clock size={11} style={{ marginLeft: 8 }} /> {formatTime(a.appointment_time)}
                    <span style={{ marginLeft: 8 }}><Icon size={11} /> {meta.label}</span>
                  </p>
                  <p className="row-meta">With {a.doctor_name}</p>
                </div>
                <span className={`status-pill status-${a.status}`}>{a.status}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Patients (read-only history)
// ---------------------------------------------------------------------------
function Reminders({ patientId, token }) {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState('');
  const [when, setWhen] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    apiGet(`/nurse/patients/${patientId}/reminders`, token).then(setItems).catch((e) => setError(e.message));
  }, [patientId, token]);
  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await apiSend('POST', '/nurse/reminders', token, { patient_id: patientId, title, remind_at: new Date(when).toISOString() });
      setTitle(''); setWhen(''); load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this reminder?')) return;
    try { await apiSend('DELETE', `/nurse/reminders/${id}`, token); load(); } catch (err) { setError(err.message); }
  };

  return (
    <div className="detail-section">
      <label className="field-label"><Bell size={12} style={{ marginRight: 4, verticalAlign: -2 }} /> Reminders</label>
      <form onSubmit={add} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
        <input className="field-input" style={{ flex: '2 1 180px' }} placeholder="Reminder (e.g. Take medication)" value={title} onChange={(e) => setTitle(e.target.value)} required />
        <input className="field-input" style={{ flex: '1 1 180px' }} type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} required />
        <button className="btn-primary" disabled={busy}><Plus size={14} /> Add</button>
      </form>
      {error && <p className="error-text">{error}</p>}
      {items.length === 0 ? <p className="muted-text">No reminders.</p> : (
        <div className="list-stack">
          {items.map((r) => (
            <div key={r.id} className="row-card">
              <div className="row-content">
                <p className="row-title" style={{ marginBottom: 2 }}>{r.title}</p>
                <p className="row-meta"><Clock size={11} /> {new Date(r.remind_at).toLocaleString()}{r.mine ? '' : ' · added by doctor'}</p>
              </div>
              {r.mine && (
                <button type="button" onClick={() => del(r.id)} title="Delete reminder" style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#B42318' }}>
                  <Trash2 size={16} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '');

function TicketDetail({ ticketId, token, onStatusChanged }) {
  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = React.useRef(null);

  useEffect(() => {
    setLoading(true); setError(null);
    apiGet(`/nurse/support/${ticketId}/messages`, token)
      .then((r) => { setTicket(r.ticket); setMessages(r.messages); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [ticketId, token]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight }); }, [messages]);

  const send = async () => {
    if (!draft.trim()) return;
    setBusy(true); setError(null);
    try {
      const r = await apiSend('POST', `/nurse/support/${ticketId}/messages`, token, { message: draft });
      setMessages(r.messages); setDraft('');
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const toggle = async () => {
    const next = ticket.status === 'resolved' ? 'open' : 'resolved';
    setBusy(true); setError(null);
    try {
      const u = await apiSend('PUT', `/nurse/support/${ticketId}/status`, token, { status: next });
      setTicket((t) => ({ ...t, status: u.status }));
      onStatusChanged(u);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (loading) return <div className="loading-row"><Loader2 size={16} className="spin" /> Loading conversation…</div>;
  if (!ticket) return <p className="error-text">{error || 'Ticket not found'}</p>;

  return (
    <div className="detail-panel">
      <div className="detail-header">
        <div className="detail-avatar">{ticket.patient_name?.[0] || '?'}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 className="detail-name" style={{ margin: 0 }}>{ticket.patient_name}</h2>
          <p className="detail-meta">{ticket.patient_email && <><Mail size={12} /> {ticket.patient_email} · </>}Ticket #{ticket.id}</p>
        </div>
        <span className={`status-pill status-${ticket.status}`}>{ticket.status}</span>
      </div>

      <div className="detail-section">
        <label className="field-label">Subject</label>
        <p className="row-desc" style={{ fontWeight: 600 }}>{ticket.subject}</p>
      </div>

      <div ref={scrollRef} style={{ overflowY: 'auto', padding: '12px 4px', minHeight: 200, maxHeight: 400, border: '1px solid #EEF0F2', borderRadius: 10, marginBottom: 12 }}>
        {messages.length === 0 ? <p className="muted-text" style={{ textAlign: 'center' }}>No messages yet.</p> : messages.map((m) => {
          const staff = m.sender_type === 'doctor';
          return (
            <div key={m.id} style={{ display: 'flex', justifyContent: staff ? 'flex-end' : 'flex-start', marginBottom: 10 }}>
              <div style={{ maxWidth: '75%', padding: '10px 14px', borderRadius: 14, background: staff ? '#0F4C75' : '#F2F3F4', color: staff ? 'white' : '#222' }}>
                <p style={{ margin: 0, fontSize: 13.5, whiteSpace: 'pre-wrap' }}>{m.message}</p>
                <p style={{ margin: '4px 0 0', fontSize: 10.5, opacity: 0.7, textAlign: 'right' }}>{fmtDateTime(m.created_at)}</p>
              </div>
            </div>
          );
        })}
      </div>

      {error && <p className="error-text">{error}</p>}

      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          className="field-input" rows={2} style={{ flex: 1, resize: 'none' }} placeholder="Type a message…"
          value={draft} disabled={busy} onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
        />
        <button onClick={send} disabled={!draft.trim() || busy} className="btn-primary"><Send size={15} /></button>
      </div>

      <div style={{ marginTop: 10 }}>
        <button
          onClick={toggle} disabled={busy} className="btn-primary"
          style={ticket.status === 'resolved' ? undefined : { background: 'white', color: '#0F4C75', border: '1px solid #0F4C75' }}
        >
          <CheckCircle2 size={15} /> {ticket.status === 'resolved' ? 'Reopen ticket' : 'Mark resolved'}
        </button>
      </div>
    </div>
  );
}

function SupportView({ token }) {
  const [statusFilter, setStatusFilter] = useState('open');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  useEffect(() => {
    setLoading(true); setError(null);
    apiGet(`/nurse/support?status=${statusFilter}`, token)
      .then((rows) => { setItems(rows); setSelectedId(rows[0]?.id ?? null); })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, statusFilter]);

  const changed = (u) => {
    if (u.status !== statusFilter) {
      setItems((prev) => { const next = prev.filter((t) => t.id !== u.id); setSelectedId(next[0]?.id ?? null); return next; });
    }
  };
  const selected = items.find((t) => t.id === selectedId) || items[0];

  return (
    <div className="section-stack">
      <div className="timeline-filters">
        <button onClick={() => setStatusFilter('open')} className={`filter-chip ${statusFilter === 'open' ? 'active' : ''}`}>Open</button>
        <button onClick={() => setStatusFilter('resolved')} className={`filter-chip ${statusFilter === 'resolved' ? 'active' : ''}`}>Resolved</button>
      </div>
      {loading ? (
        <div className="loading-row"><Loader2 size={16} className="spin" /> Loading…</div>
      ) : error ? (
        <p className="error-text">{error}</p>
      ) : items.length === 0 ? (
        <p className="muted-text">No {statusFilter} tickets.</p>
      ) : (
        <div className="master-detail">
          <div className="appt-list-col">
            {items.map((t) => (
              <button key={t.id} onClick={() => setSelectedId(t.id)} className={`appt-list-item ${t.id === selected.id ? 'active' : ''}`}>
                <div className="appt-list-avatar">{t.patient_name?.[0] || '?'}</div>
                <div className="appt-list-info">
                  <p className="appt-list-name" style={{ margin: 0 }}>{t.subject} <span style={{ fontWeight: 400, color: '#888780' }}>#{t.id}</span></p>
                  <p className="appt-list-time">{t.patient_name} · {fmtDateTime(t.last_message_at || t.created_at)}</p>
                </div>
              </button>
            ))}
          </div>
          <div className="detail-col">
            {selected && <TicketDetail key={selected.id} ticketId={selected.id} token={token} onStatusChanged={changed} />}
          </div>
        </div>
      )}
    </div>
  );
}

function PatientDetail({ patientId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    apiGet(`/nurse/patients/${patientId}/history`, token)
      .then(setData)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, patientId]);

  if (loading) return <div className="loading-row"><Loader2 size={16} className="spin" /> Loading…</div>;
  if (error) return <p className="error-text">{error}</p>;
  const p = data.patient;

  return (
    <div className="detail-panel">
      <div className="detail-header">
        <div className="detail-avatar">{p.first_name?.[0] || '?'}</div>
        <div>
          <h2 className="detail-name" style={{ margin: 0 }}>{p.first_name} {p.last_name}</h2>
          <p className="detail-meta"><Mail size={12} /> {p.email}</p>
        </div>
      </div>

      {p.phone && (
        <div className="detail-row">
          <span className="detail-label">Phone</span>
          <span className="detail-value">{formatPhoneDisplay({ phone: p.phone, phoneCountryCode: p.phone_country_code })}</span>
        </div>
      )}

      <Reminders patientId={patientId} token={token} />

      <div className="detail-section">
        <label className="field-label"><ClipboardList size={12} style={{ marginRight: 4, verticalAlign: -2 }} /> Visit history</label>
        <div className="list-stack">
          {data.appointments.map((h) => (
            <div key={h.id} className="row-card" style={{ alignItems: 'flex-start' }}>
              <div className="row-content">
                <p className="row-title" style={{ marginBottom: 2 }}>
                  {formatDate(h.appointment_date)} · {formatTime(h.appointment_time)}
                </p>
                <p className="row-meta">{h.doctor_name}{h.doctor_specialty ? ` · ${h.doctor_specialty}` : ''}</p>
                {h.notes && (
                  <p style={{ margin: '8px 0 0', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    <strong>Notes:</strong> {h.notes}
                  </p>
                )}
                {h.follow_up_plan && (
                  <p style={{ margin: '6px 0 0', fontSize: 13, whiteSpace: 'pre-wrap' }}>
                    <strong>Follow-up:</strong> {h.follow_up_plan}
                  </p>
                )}
              </div>
              <span className={`status-pill status-${h.status}`}>{h.status}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function PatientsView({ token, openId }) {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(openId || null);

  useEffect(() => { if (openId) setSelectedId(openId); }, [openId]);

  const load = useCallback((q) => {
    setLoading(true);
    setError(null);
    apiGet(`/nurse/patients${q ? `?q=${encodeURIComponent(q)}` : ''}`, token)
      .then((rows) => {
        setItems(rows);
        setSelectedId((prev) => (prev && (rows.some((p) => p.id === prev) || prev === openId) ? prev : rows[0]?.id ?? null));
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [token, openId]);

  useEffect(() => { load(''); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => load(query.trim()), 300);
    return () => clearTimeout(t);
  }, [query, load]);

  return (
    <div className="section-stack">
      <div style={{ position: 'relative', maxWidth: 360 }}>
        <Search size={14} style={{ position: 'absolute', left: 10, top: 11, color: '#9CA3AF' }} />
        <input
          className="field-input"
          style={{ paddingLeft: 32 }}
          placeholder="Search by name or email"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>

      {loading && items.length === 0 ? (
        <div className="loading-row"><Loader2 size={16} className="spin" /> Loading…</div>
      ) : error ? (
        <p className="error-text">{error}</p>
      ) : items.length === 0 ? (
        <p className="muted-text">No patients found.</p>
      ) : (
        <div className="master-detail">
          <div className="appt-list-col">
            {items.map((p) => (
              <button
                key={p.id}
                onClick={() => setSelectedId(p.id)}
                className={`appt-list-item ${p.id === selectedId ? 'active' : ''}`}
              >
                <div className="appt-list-avatar">{p.first_name?.[0] || '?'}</div>
                <div className="appt-list-info">
                  <p className="appt-list-name" style={{ margin: 0 }}>{p.first_name} {p.last_name}</p>
                  <p className="appt-list-time">{p.email}</p>
                </div>
              </button>
            ))}
          </div>
          <div className="detail-col">
            {selectedId && <PatientDetail key={selectedId} patientId={selectedId} token={token} />}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const NurseDashboard = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('nurse_token');

  const [nurse, setNurse] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nurse_info') || 'null'); } catch (e) { return null; }
  });
  const [tab, setTab] = useState('upcoming');
  const [openPatientId, setOpenPatientId] = useState(null);
  const [inactive, setInactive] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const logout = useCallback(() => {
    localStorage.removeItem('nurse_token');
    localStorage.removeItem('nurse_info');
    navigate('/doctor/login', { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (!token) {
      navigate('/doctor/login', { replace: true });
      return;
    }
    apiGet('/nurse/me', token)
      .then(setNurse)
      .catch((err) => {
        if (err.status === 401) logout();
        else if (err.body?.code === 'NURSE_INACTIVE') setInactive(true);
        else setLoadError(err.message);
      });
  }, [token, navigate, logout]);

  if (!token) return null;

  if (inactive) {
    return (
      <div className="dd-page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', maxWidth: 420, padding: 16 }}>
          <AlertCircle size={32} style={{ color: '#B42318', marginBottom: 8 }} />
          <h2 style={{ margin: '0 0 8px', fontSize: 18 }}>Your account is switched off</h2>
          <p className="muted-text" style={{ marginBottom: 16 }}>
            Please contact your admin or doctor to have it turned back on.
          </p>
          <button onClick={logout} className="btn-primary">Log out</button>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="dd-page" style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <p className="error-text" style={{ marginBottom: 12 }}>{loadError}</p>
          <button onClick={logout} className="btn-primary">Back to login</button>
        </div>
      </div>
    );
  }

  return (
    <div className="dd-page">
      <Sidebar nurse={nurse} activeTab={tab} onTabChange={(t) => { setOpenPatientId(null); setTab(t); }} onLogout={logout} />

      <main className="dd-main">
        <div className="dd-main-header">
          <h1 className="dd-main-title">
            {TAB_TITLES[tab]}
          </h1>
        </div>

        <div className="dd-main-content">
          <ReadOnlyBanner />
          {nurse && nurse.doctors && nurse.doctors.length === 0 && (
            <p className="muted-text">No doctor is linked to your account yet, so there is nothing to show. Ask your admin or doctor.</p>
          )}
          {APPT_TABS.includes(tab) && (
            <AppointmentsView
              key={tab}
              status={tab}
              token={token}
              onOpenPatient={(id) => { setOpenPatientId(id); setTab('patients'); }}
            />
          )}
          {tab === 'patients' && <PatientsView token={token} openId={openPatientId} />}
          {tab === 'support' && <SupportView token={token} />}
        </div>
      </main>
    </div>
  );
};

export default NurseDashboard;